import { useCallback, useMemo, useState } from 'react'

import { FileListItem } from '@views/documents/elements/FileList'

import { UNSORTED_FOLDER_ID } from '../constants'
import { mapDocumentToFileListItem } from '../utils/mapDocumentToFileListItem'
import { useDeleteItems } from './useDeleteItems'
import { useFetchFolderContents } from './useFetchFolderContents'

interface UseDocumentListStateOptions {
  onDeleteSuccess?: () => void
}

export const useDocumentListState = (options: UseDocumentListStateOptions = {}) => {
  const { onDeleteSuccess } = options
  const { documents, isEmpty, isFolderContentsLoading } = useFetchFolderContents(UNSORTED_FOLDER_ID)

  const items: FileListItem[] = useMemo(() => documents.map(mapDocumentToFileListItem), [documents])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const filteredItems = useMemo(
    () => (searchQuery ? items.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase())) : items),
    [items, searchQuery],
  )

  const { deleteItem, deleteBulkItems } = useDeleteItems({
    items: filteredItems,
    onSuccess: () => {
      setSelectedIds([])
      onDeleteSuccess?.()
    },
  })

  const handleCheckedChange = useCallback((ids: string[]) => {
    setSelectedIds(ids)
  }, [])

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  return useMemo(
    () => ({
      documents,
      items,
      isEmpty,
      isFolderContentsLoading,
      filteredItems,
      selectedIds,
      searchQuery,
      deleteItem,
      deleteBulkItems,
      handleCheckedChange,
      handleSearchChange,
    }),
    [
      documents,
      items,
      isEmpty,
      isFolderContentsLoading,
      filteredItems,
      selectedIds,
      searchQuery,
      deleteItem,
      deleteBulkItems,
      handleCheckedChange,
      handleSearchChange,
    ],
  )
}
